import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  Plus,
  Users,
  Shield,
  Package,
  ToggleLeft,
  ToggleRight,
  User
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StaffManagement() {
  const { staff: currentStaff } = useAuth();
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: "",
    password: "",
    first_name: "",
    last_name: "",
    role: "MATERIALAUSGABE"
  });
  const [createLoading, setCreateLoading] = useState(false);

  const fetchStaff = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/staff`);
      setStaffList(response.data);
    } catch (error) {
      console.error("Error fetching staff:", error);
      toast.error("Fehler beim Laden der Mitarbeiter");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const handleCreate = async () => {
    if (!createForm.username || !createForm.password || !createForm.first_name || !createForm.last_name) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }

    setCreateLoading(true);
    try {
      await axios.post(`${API}/staff`, createForm);
      toast.success("Mitarbeiter erfolgreich angelegt");
      setShowCreateModal(false);
      setCreateForm({
        username: "",
        password: "",
        first_name: "",
        last_name: "",
        role: "MATERIALAUSGABE"
      });
      fetchStaff();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Anlegen";
      toast.error(message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleToggleStatus = async (staffId) => {
    try {
      await axios.put(`${API}/staff/${staffId}/toggle`);
      toast.success("Status geändert");
      fetchStaff();
    } catch (error) {
      toast.error("Fehler beim Ändern des Status");
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleDateString("de-DE");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="staff-management">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Zurück
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-xl font-bold text-slate-900">
                  Personalverwaltung
                </h1>
                <p className="text-sm text-slate-500">Admin & Materialausgabe Konten</p>
              </div>
            </div>
            
            <Button onClick={() => setShowCreateModal(true)} data-testid="create-staff-btn">
              <Plus className="h-4 w-4 mr-2" />
              Neuer Mitarbeiter
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Mitarbeiterliste
              <Badge variant="outline" className="ml-2">{staffList.length} Einträge</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Benutzername</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Rolle</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffList.map((staff) => (
                    <TableRow key={staff.id}>
                      <TableCell className="font-mono">{staff.username}</TableCell>
                      <TableCell>{staff.first_name} {staff.last_name}</TableCell>
                      <TableCell>
                        <Badge className={staff.role === "ADMIN" ? "bg-purple-500" : "bg-blue-500"}>
                          {staff.role === "ADMIN" ? (
                            <><Shield className="h-3 w-3 mr-1" /> Admin</>
                          ) : (
                            <><Package className="h-3 w-3 mr-1" /> Materialausgabe</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={staff.is_active ? "default" : "secondary"}>
                          {staff.is_active ? "Aktiv" : "Inaktiv"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500">{formatDate(staff.created_at)}</TableCell>
                      <TableCell className="text-right">
                        {staff.id !== currentStaff?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(staff.id)}
                            title={staff.is_active ? "Deaktivieren" : "Aktivieren"}
                          >
                            {staff.is_active ? (
                              <ToggleRight className="h-5 w-5 text-emerald-500" />
                            ) : (
                              <ToggleLeft className="h-5 w-5 text-slate-400" />
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Neuen Mitarbeiter anlegen
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vorname *</Label>
                <Input
                  value={createForm.first_name}
                  onChange={(e) => setCreateForm({...createForm, first_name: e.target.value})}
                  placeholder="Vorname"
                />
              </div>
              <div className="space-y-2">
                <Label>Nachname *</Label>
                <Input
                  value={createForm.last_name}
                  onChange={(e) => setCreateForm({...createForm, last_name: e.target.value})}
                  placeholder="Nachname"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Benutzername *</Label>
              <Input
                value={createForm.username}
                onChange={(e) => setCreateForm({...createForm, username: e.target.value})}
                placeholder="Benutzername"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Passwort *</Label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                placeholder="Passwort"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Rolle *</Label>
              <Select
                value={createForm.role}
                onValueChange={(value) => setCreateForm({...createForm, role: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="MATERIALAUSGABE">Materialausgabe</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreate} disabled={createLoading}>
              {createLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                "Anlegen"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
